import {
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { SwPush } from '@angular/service-worker';
import { TranslatePipe } from '@ngx-translate/core';
import { NotificationApi } from '@tvd/data-access/notifications';
import { TVDFormField } from '@tvd/design-system/form-field';
import { Toggle } from '@tvd/design-system/toggle';
import { Storage } from '@tvd/utils/storage';
import { EMPTY, catchError, filter, map, switchMap, take } from 'rxjs';

function safeHost(endpoint: string | undefined | null): string | null {
  if (!endpoint) return null;
  try {
    return new URL(endpoint).host;
  } catch {
    return 'invalid-url';
  }
}

@Component({
  selector: 'tvd-push-toggle',
  imports: [TranslatePipe, TVDFormField, Toggle],
  template: `
    <tvd-form-field class="w-full">
      <label for="pushToggle">
        {{ 'notification.settings.push.label' | translate }}
      </label>
      <tvd-toggle
        id="pushToggle"
        [checked]="pushEnabled()"
        [disabled]="pushBlocked()"
        (checkedChange)="onPushToggle($event)"
        data-testid="push-toggle"
      />
      @if (pushBlocked()) {
        <small class="text-muted">
          {{ 'notification.settings.push.blocked' | translate }}
        </small>
      }
    </tvd-form-field>
  `,
})
export class PushToggle {
  readonly #swPush = inject(SwPush);
  readonly #api = inject(NotificationApi);
  readonly #storage = inject(Storage);

  readonly #subscription = toSignal(this.#swPush.subscription);
  readonly #optedIn = this.#storage.getAsSignal('PUSH_OPTED_IN', {
    initialValue: false,
  });
  readonly pushEnabled = computed(
    () => this.#subscription() != null || this.#optedIn(),
  );
  readonly pushBlocked = signal(false);

  constructor() {
    if ('Notification' in globalThis) {
      this.pushBlocked.set(Notification.permission === 'denied');
    }

    console.log('[push-toggle] init', {
      notificationPermission:
        'Notification' in globalThis ? Notification.permission : 'n/a',
      optedIn: this.#optedIn(),
      pushBlocked: this.pushBlocked(),
    });

    if (this.#optedIn() && !this.pushBlocked()) {
      this.#swPush.subscription.pipe(take(1)).subscribe((sub) => {
        console.log('[push-toggle] init subscription check', {
          hasSubscription: sub != null,
          endpointHost: sub ? safeHost(sub.endpoint) : null,
        });
        if (!sub) this.#subscribe();
      });
    }
  }

  #subscribe() {
    console.log('[push-toggle] #subscribe start', {
      notificationPermission:
        'Notification' in globalThis ? Notification.permission : 'n/a',
    });
    this.#api
      .getVapidPublicKey()
      .pipe(
        switchMap(({ publicKey }) => {
          console.log(
            '[push-toggle] got VAPID public key, requesting subscription',
            {
              publicKeyLength: publicKey?.length,
            },
          );
          return this.#swPush.requestSubscription({
            serverPublicKey: publicKey,
          });
        }),
        catchError((err: unknown) => {
          const e = err as { name?: string; message?: string };
          console.error('[push-toggle] subscribe failed', {
            name: e?.name,
            message: e?.message,
            notificationPermission:
              'Notification' in globalThis ? Notification.permission : 'n/a',
          });
          this.pushBlocked.set(
            'Notification' in globalThis &&
              Notification.permission === 'denied',
          );
          this.#optedIn.set(false);
          return EMPTY;
        }),
        map((sub) => {
          console.log('[push-toggle] requestSubscription returned', {
            hasEndpoint: !!sub.endpoint,
            endpointHost: safeHost(sub.endpoint),
          });
          return sub.toJSON();
        }),
        filter(
          (res): res is PushSubscriptionJSON => !!res.endpoint && !!res.keys,
        ),
        switchMap((res) => {
          console.log('[push-toggle] saving subscription to backend', {
            endpointHost: safeHost(res.endpoint),
          });
          return this.#api.savePushSubscription({
            endpoint: res.endpoint || '',
            keys: {
              p256dh: res?.keys?.['p256dh'] || '',
              auth: res?.keys?.['auth'] || '',
            },
          });
        }),
      )
      .subscribe({
        next: () => console.log('[push-toggle] savePushSubscription ok'),
        error: (err: unknown) => {
          const e = err as { name?: string; message?: string };
          console.error('[push-toggle] savePushSubscription failed', {
            name: e?.name,
            message: e?.message,
          });
        },
      });
  }

  onPushToggle(enable: boolean | null) {
    if (enable) {
      this.#optedIn.set(true);
      this.#subscribe();
    } else {
      this.#optedIn.set(false);
      const sub = this.#subscription();
      if (sub) {
        const endpoint = sub.endpoint;
        sub.unsubscribe().then(() => {
          this.#api.deletePushSubscription({ endpoint }).subscribe();
        });
      }
    }
  }
}
